// Network resilience for API calls: a per-request timeout, exponential backoff
// with jitter, and a clear "offline" error the UI can show.
//
// Retry policy (by default):
//   - Connection failures (no response at all: DNS, offline, reset, timeout)
//     are retried for every method. If nothing reached the server, replaying
//     a POST cannot duplicate anything.
//   - 502 / 503 / 504 (server briefly unavailable) are retried only for safe
//     methods (GET / HEAD); a mutation that got a gateway error may already
//     have been processed, so it is surfaced instead.
//   - 4xx and other 5xx are never retried.
// Callers can pass `retry: false` to opt out, or `retry: { retries, timeoutMs }`.

export const NETWORK_ERROR_CODE = 'network_error';
export const OFFLINE_MESSAGE = "You appear to be offline. Check your connection and try again.";

const DEFAULT_RETRIES = 2;          // 3 attempts total
const DEFAULT_TIMEOUT_MS = 15000;
const BASE_DELAY_MS = 600;
const MAX_DELAY_MS = 4000;
const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const SAFE_METHODS = new Set(['GET', 'HEAD']);

let networkErrorHandler = null;
let lastHandlerCallAt = 0;
const HANDLER_THROTTLE_MS = 20000;

/** Register a UI-level callback (banner / toast) invoked when a request gives up because of the network. */
export function setNetworkErrorHandler(fn) {
    networkErrorHandler = fn;
}

function notifyNetworkError(error) {
    if (!networkErrorHandler) return;
    const now = Date.now();
    if (now - lastHandlerCallAt < HANDLER_THROTTLE_MS) return;
    lastHandlerCallAt = now;
    try { networkErrorHandler(error); } catch { /* never let UI code break a request */ }
}

/** Fetch rejected without any HTTP response: offline, DNS, TLS, reset, or our own timeout. */
export function isNetworkError(error) {
    if (!error) return false;
    if (error.name === 'AbortError' || error.name === 'TimeoutError') return true;
    if (error.code === NETWORK_ERROR_CODE) return true;
    const message = String(error.message || '').toLowerCase();
    return error.name === 'TypeError' && (
        message.includes('network request failed')
        || message.includes('failed to fetch')
        || message.includes('load failed')
        || message.includes('networkerror')
        || message.includes('network error')
        || message.includes('internet')
    );
}

export function isRetryableStatus(status) {
    return RETRYABLE_STATUSES.has(status);
}

/** Exponential backoff with full jitter: 0–600ms, 0–1200ms, 0–2400ms … capped. */
export function backoffDelay(attempt, base = BASE_DELAY_MS, max = MAX_DELAY_MS) {
    const ceiling = Math.min(max, base * (2 ** attempt));
    return Math.round(Math.random() * ceiling);
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export class NetworkRequestError extends Error {
    constructor(message = OFFLINE_MESSAGE, { cause = null, attempts = 1 } = {}) {
        super(message);
        this.name = 'NetworkRequestError';
        this.code = NETWORK_ERROR_CODE;
        this.statusCode = 0;
        this.attempts = attempts;
        this.cause = cause;
    }
}

export function resolveRetryOptions(method = 'GET', retry) {
    if (retry === false) return { retries: 0, timeoutMs: DEFAULT_TIMEOUT_MS, retryStatuses: false };
    const upper = String(method).toUpperCase();
    return {
        retries: Number.isInteger(retry?.retries) ? retry.retries : DEFAULT_RETRIES,
        timeoutMs: Number.isInteger(retry?.timeoutMs) ? retry.timeoutMs : DEFAULT_TIMEOUT_MS,
        // Gateway errors are only replayed for safe methods.
        retryStatuses: retry?.retryStatuses ?? SAFE_METHODS.has(upper),
    };
}

/**
 * Runs `doFetch(signal)` with a timeout and the retry policy above.
 * Resolves with the Response (any status) or throws NetworkRequestError once
 * every attempt failed to get a response.
 */
export async function fetchWithRetry(doFetch, { method = 'GET', retry, onRetry, sleepFn = sleep, notify = true } = {}) {
    const { retries, timeoutMs, retryStatuses } = resolveRetryOptions(method, retry);
    let lastError = null;

    for (let attempt = 0; attempt <= retries; attempt += 1) {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
        try {
            const response = await doFetch(controller?.signal);
            if (retryStatuses && isRetryableStatus(response.status) && attempt < retries) {
                onRetry?.({ attempt: attempt + 1, reason: `http_${response.status}` });
                await sleepFn(backoffDelay(attempt));
                continue;
            }
            return response;
        } catch (error) {
            if (!isNetworkError(error)) throw error;
            lastError = error;
            if (attempt < retries) {
                onRetry?.({ attempt: attempt + 1, reason: error.name === 'AbortError' ? 'timeout' : 'network' });
                await sleepFn(backoffDelay(attempt));
                continue;
            }
        } finally {
            if (timer) clearTimeout(timer);
        }
    }

    const failure = new NetworkRequestError(OFFLINE_MESSAGE, { cause: lastError, attempts: retries + 1 });
    // Background calls (diagnostics delivery) pass notify: false so they never
    // raise the "you're offline" banner on their own.
    if (notify) notifyNetworkError(failure);
    throw failure;
}
